import { notifications } from '@mantine/notifications';
import { IconMoodPuzzled } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Endpoints } from '../../apis';
import { isNetworkError } from '../../libs/connectionNotifier';
import { keepIfSameProject } from '../../utils/projects';
import { useActiveProjectScope, useProductAccess } from '../useActiveProject';

interface UseBaremetalListQueryOptions {
    refetchInterval?: number | false;
}

export function useBaremetalListQuery({
    refetchInterval = false,
}: UseBaremetalListQueryOptions = {}) {
    const { t } = useTranslation('notifications');
    const hasShownError = useRef(false);
    const { activeProjectId, isResolved } = useActiveProjectScope();
    const hasNoAccess = useProductAccess('baremetal') === 'none';

    const query = useQuery({
        queryKey: ['api.v1.baremetals.list', activeProjectId],
        queryFn: async () => Endpoints.v1.Baremetals.getBaremetals(),
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchInterval,
        gcTime: 1000 * 60 * 60 * 24,
        placeholderData: keepIfSameProject(activeProjectId),
        enabled: isResolved && !hasNoAccess,
    });

    const { data, isPending, error, refetch, isRefetching } = query;

    useEffect(() => {
        if (error && !isNetworkError(error) && !hasShownError.current) {
            hasShownError.current = true;
            notifications.show({
                title: t('query.failed'),
                message: t('query.baremetalFailedMessage'),
                icon: <IconMoodPuzzled size={32} color={'#eb5757'} />,
            });
        }

        if (!error) {
            hasShownError.current = false;
        }
    }, [error, t]);

    const collective = useMemo(() => {
        return (data ?? []).reduce(
            (summary, b) => {
                summary.servers += 1;
                summary.monthly += b.price_monthly;
                if (b.auto_renew) summary.autoRenewOn += 1;
                else summary.autoRenewOff += 1;
                return summary;
            },
            { servers: 0, monthly: 0, autoRenewOn: 0, autoRenewOff: 0 }
        );
    }, [data]);

    return {
        data,
        isPending: isPending && !hasNoAccess,
        collective,
        refetch,
        isRefetching,
    };
}
